import { connectionSentence } from './ConnectionPathCard'
import type {
  ConnectionEdge,
  ConnectionNode,
  ConnectionPath,
  ConnectionResult,
} from '../../services/apiClient'

export type SharedTag = 'partnership' | 'names'

/** One company (or person) both ends share directly: A – middle – B. */
export interface SharedLink {
  middle: ConnectionNode
  path: ConnectionPath
  /** The edge on the »fra« side and on the »til« side. */
  fromEdge: ConnectionEdge
  toEdge: ConnectionEdge
  /** 1–3, the same scale as the network tab: 3 owned or ran it together. */
  level: 1 | 2 | 3
  tags: SharedTag[]
  /** Orders links within a level. */
  rank: number
  /** Each end also reaches the middle through these (its holding company, say). */
  fromVia: Via[]
  toVia: Via[]
}

/** A detour to the shared middle: the nodes on the way, and the link into the middle. */
export interface Via {
  nodes: ConnectionNode[]
  edge: ConnectionEdge
}

export interface ConnectionSummary {
  /** One link: the two are directly related (owner of, director in …). */
  direct: ConnectionPath | null
  /** Strongest first. */
  shared: SharedLink[]
  /** Chains of two or more links, shortest first. */
  longer: ConnectionPath[]
}

const PARTNERSHIP =
  /(^|\s)(P\/S|I\/S|K\/S)(\s|$)|partnerselskab|interessentskab|kommanditselskab/i

type Role = 'owner' | 'management' | 'board' | 'founder' | 'other'

const isFormer = (e: ConnectionEdge) => e.current === false

function role(e: ConnectionEdge): Role {
  if (e.kind === 'ownership') return 'owner'
  if (e.label.includes('direktion')) return 'management'
  if (e.label.includes('bestyrelse')) return 'board'
  if (e.label.startsWith('stiftede')) return 'founder'
  return 'other'
}

/** Mirrors PersonNetwork's levels: own or run it together = 3, board = 2. */
function pairLevel(a: Role, b: Role): 1 | 2 | 3 {
  const strong = (r: Role) => r === 'owner' || r === 'management'
  if (strong(a) && strong(b)) return 3
  if (a === 'founder' && b === 'founder') return 3
  if (a === 'other' || b === 'other') return 1
  return 2
}

/** Highest ownership share in the label (»ejer 50–66,66 % af« → 66.66). */
function share(e: ConnectionEdge): number {
  const nums = e.label.match(/\d+(?:,\d+)?/g) ?? []
  return Math.max(0, ...nums.map((n) => parseFloat(n.replace(',', '.'))))
}

/** Surnames and middle names long enough to mean something in a company name. */
function nameParts(name: string): string[] {
  return name
    .split(/\s+/)
    .slice(1)
    .filter((p) => p.length >= 4)
    .map((p) => p.toLowerCase())
}

function carriesName(company: string, person: ConnectionNode): boolean {
  if (person.kind !== 'person') return false
  const words = new Set(company.toLowerCase().split(/[^\p{L}]+/u))
  return nameParts(person.name).some((p) => words.has(p))
}

function sharedLink(path: ConnectionPath): SharedLink {
  const [a, middle, b] = path.nodes
  const [fromEdge, toEdge] = path.edges
  const ra = role(fromEdge)
  const rb = role(toEdge)
  const tags: SharedTag[] = []
  if (
    middle.kind !== 'person' &&
    ra === 'owner' &&
    rb === 'owner' &&
    PARTNERSHIP.test(middle.name)
  ) {
    tags.push('partnership')
  }
  if (middle.kind !== 'person' && carriesName(middle.name, a) && carriesName(middle.name, b)) {
    tags.push('names')
  }
  const level = pairLevel(ra, rb)
  return {
    middle,
    path,
    fromEdge,
    toEdge,
    level,
    tags,
    fromVia: [],
    toVia: [],
    // Current roles before ended ones, then strength.
    rank:
      level * 10 +
      tags.length * 4 +
      Math.min(share(fromEdge), share(toEdge)) / 100 -
      [fromEdge, toEdge].filter(isFormer).length * 30,
  }
}

/**
 * Both ends sit in the middle company, or the middle person sits in both
 * end companies. »Person → Holding → Selskab« is a chain, not shared.
 */
function sharesMiddle(p: ConnectionPath): boolean {
  const middle = p.nodes[1].id
  const [x, y] = p.edges
  return (x.to === middle && y.to === middle) || (x.from === middle && y.from === middle)
}

/**
 * Splits a search result into what a reader needs first: a direct link,
 * the companies or people both share (strongest first), then the longer
 * chains.
 */
export function summarize(result: ConnectionResult): ConnectionSummary {
  const direct = result.paths.find((p) => p.edges.length === 1) ?? null
  const isShared = (p: ConnectionPath) => p.edges.length === 2 && sharesMiddle(p)
  const shared = result.paths
    .filter(isShared)
    .map(sharedLink)
    .sort((x, y) => y.rank - x.rank)
  const byMiddle = new Map(shared.map((s) => [s.middle.id, s]))
  const longer: ConnectionPath[] = []
  for (const p of result.paths) {
    if (p.edges.length <= 1 || isShared(p)) continue
    if (!addDetour(p, byMiddle)) longer.push(p)
  }
  return { direct, shared, longer }
}

/**
 * A longer path through a shared middle is a detour of that link: the ends
 * also reach it through other companies. Kept on the link, one per company.
 */
function addDetour(p: ConnectionPath, byMiddle: Map<string, SharedLink>): boolean {
  const at = p.nodes.findIndex((n, i) => i > 0 && i < p.nodes.length - 1 && byMiddle.has(n.id))
  if (at < 0) return false
  const link = byMiddle.get(p.nodes[at].id)!
  // One company on each side at most; a longer detour adds nothing to read.
  if (at > 2 || p.nodes.length - 1 - at > 2) return true
  const add = (list: Via[], nodes: ConnectionNode[], edge: ConnectionEdge) => {
    if (nodes.length === 0) return
    const key = nodes.map((n) => n.id).join('>')
    if (!list.some((v) => v.nodes.map((n) => n.id).join('>') === key)) list.push({ nodes, edge })
  }
  add(link.fromVia, p.nodes.slice(1, at), p.edges[at - 1])
  add(link.toVia, p.nodes.slice(at + 1, -1).reverse(), p.edges[at])
  return true
}

/** »Thomas også via Nørager Holding ApS, der ejede 5–9,99 % (2025)«. */
export function viaText(end: ConnectionNode, via: Via): string {
  const last = via.nodes[via.nodes.length - 1]
  const how = via.edge.from === last.id ? `, der ${verb(via.edge)}` : ''
  return `${firstName(end)} også via ${last.name}${how}`
}

/** »ejer 50–66,66 % af« → »ejer 50–66,66 %«: the verb without its object, with the years of an ended role. */
export function verb(e: ConnectionEdge): string {
  const v = e.label.replace(/\s+(af|i|for)$/, '')
  return e.period ? `${v} (${e.period})` : v
}

function firstName(n: ConnectionNode): string {
  return n.kind === 'person' ? n.name.split(/\s+/)[0] : n.name
}

/** How each end sits in the shared company: »Ossama ejer 50 % · Benjamin er reel ejer«. */
export function sharedRoles(link: SharedLink): string {
  const [a, , b] = link.path.nodes
  if (link.middle.kind === 'person') {
    return `${verb(link.fromEdge)} ${a.name} · ${verb(link.toEdge)} ${b.name}`
  }
  if (link.fromEdge.label === link.toEdge.label && link.fromEdge.period === link.toEdge.period) {
    const v = verb(link.fromEdge)
    return `Begge ${v === 'er reel ejer' ? 'er reelle ejere' : v}`
  }
  return `${firstName(a)} ${verb(link.fromEdge)} · ${firstName(b)} ${verb(link.toEdge)}`
}

/** The headline: what the two are to each other, from the strongest link. */
export function headline(
  summary: ConnectionSummary,
  from: ConnectionNode,
  to: ConnectionNode
): { title: string; detail: string } | null {
  const { direct, shared } = summary
  const top = shared[0]
  const companies = shared.filter((s) => s.middle.kind !== 'person')
  const owned = companies.filter(
    (s) => role(s.fromEdge) === 'owner' && role(s.toEdge) === 'owner'
  ).length
  const together =
    owned > 0
      ? `De ejer ${owned === 1 ? 'ét selskab' : `${owned} selskaber`} sammen`
      : companies.length > 0
        ? `De er begge med i ${companies.length === 1 ? 'ét selskab' : `${companies.length} selskaber`}`
        : shared.length > 0
          ? `${shared.length === 1 ? 'Én person' : `${shared.length} personer`} går igen i begge`
          : ''

  if (direct) {
    const e = direct.edges[0]
    const owner = e.from === from.id ? from : to
    const owned2 = e.from === from.id ? to : from
    return {
      title: `${owner.name} ${e.label} ${owned2.name}`,
      detail: together ? `${together}.` : 'Direkte forbindelse i CVR.',
    }
  }
  if (!top) return null
  if (isFormer(top.fromEdge) || isFormer(top.toEdge)) {
    const both = isFormer(top.fromEdge) && isFormer(top.toEdge)
    return {
      title: top.tags.includes('partnership') && both
        ? `Tidligere partnere i ${top.middle.name}`
        : top.middle.kind === 'person'
          ? `Forbundet gennem ${top.middle.name}`
          : `Begge har haft roller i ${top.middle.name}`,
      detail:
        companies.length > 1
          ? `${companies.length} selskaber med roller fra dem begge, mindst én af dem ophørt.`
          : 'Mindst én af rollerne er ophørt, så de har ikke nødvendigvis været der samtidig.',
    }
  }
  if (top.tags.includes('partnership')) {
    return { title: `Partnere i ${top.middle.name}`, detail: `${together}.` }
  }
  if (top.middle.kind === 'person') {
    return { title: `Forbundet gennem ${top.middle.name}`, detail: `${together}.` }
  }
  const verbTop =
    top.level === 3 && owned > 0
      ? `Ejer ${top.middle.name} sammen`
      : role(top.fromEdge) === 'board' && role(top.toEdge) === 'board'
        ? `Sidder i bestyrelsen for ${top.middle.name} sammen`
        : `Begge med i ${top.middle.name}`
  return { title: verbTop, detail: `${together}.` }
}

/** The whole overview as text to paste. */
export function summaryText(
  summary: ConnectionSummary,
  from: ConnectionNode,
  to: ConnectionNode
): string {
  const head = headline(summary, from, to)
  const lines = summary.shared.flatMap((s) => [
    connectionSentence(s.path),
    ...s.fromVia.map((v) => `${viaText(s.path.nodes[0], v)}.`),
    ...s.toVia.map((v) => `${viaText(s.path.nodes[2], v)}.`),
  ])
  return [head ? `${head.title}. ${head.detail}` : '', ...lines]
    .filter(Boolean)
    .join('\n')
}

export const TAG_LABEL: Record<SharedTag, string> = {
  partnership: 'Partnerskab',
  names: 'Bærer begges navne',
}
